import { useState, useCallback, useMemo } from 'react';

import { useGetMySignQuery } from '@features/employee/hooks';
import safeToBase64Image from '@shared/lib/safeToBase64Image';
import Button from '@shared/components/atoms/Button';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import { useIsMobile } from '@shared/hooks';
import SignSettingModal from './components/SignSettingModal/SignSettingModal';
import { MobileLayout } from '@shared/components/organisms/mobile';
import MobileSignSettingContent from '@pages/settings/components/mobile/MobileSignSettingContent';
import MobileSignEditScreen from '@pages/settings/components/mobile/MobileSignEditScreen';

import styles from './SignSettingPage.module.scss';

/**
 * SignSettingPage 컴포넌트
 * - 서명설정 페이지
 */
function SignSettingPage() {
  // 모바일 화면 감지
  const isMobile = useIsMobile();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditScreenOpen, setIsEditScreenOpen] = useState(false);
  const [isNotificationScreenOpen, setIsNotificationScreenOpen] = useState(false);

  // 서명 조회
  const { data: signData, isLoading, refetch: refetchSign } = useGetMySignQuery();

  const signRaw = signData?.resultData?.signImage ?? null;

  // 서명 이미지 base64 변환
  const signImageUrl = useMemo(() => {
    return safeToBase64Image(signRaw);
  }, [signRaw]);

  /**
   * 등록 버튼 클릭 핸들러
   * - PC: 서명설정 모달을 열어 새 서명을 등록할 수 있도록 함
   * - 모바일: 서명 수정 화면으로 전환
   */
  const handleRegister = useCallback(
    function () {
      if (isMobile) {
        setIsEditScreenOpen(true);
      } else {
        setIsModalOpen(true);
      }
    },
    [isMobile],
  );

  /**
   * 모달 닫기 핸들러
   * - 서명설정 모달을 닫음
   */
  const handleCloseModal = useCallback(function () {
    setIsModalOpen(false);
  }, []);

  /**
   * 모바일 수정 화면 닫기 핸들러
   */
  const handleCloseEditScreen = useCallback(function () {
    setIsEditScreenOpen(false);
  }, []);

  /**
   * 서명 등록 성공 콜백
   * - 서명 등록이 성공하면 서명 정보를 새로고침하여 화면에 반영
   */
  const handleSignSuccess = useCallback(
    function () {
      refetchSign();
    },
    [refetchSign],
  );

  // 모바일 레이아웃 렌더링
  if (isMobile) {
    return (
      <>
        <MobileLayout title="서명설정">
          <MobileSignSettingContent
            signImageUrl={signImageUrl}
            isLoading={isLoading}
            onRegister={handleRegister}
          />
        </MobileLayout>

        {/* 모바일 서명 수정 화면 */}
        <MobileSignEditScreen
          isOpen={isEditScreenOpen}
          onClose={handleCloseEditScreen}
          onSuccess={handleSignSuccess}
          signImageUrl={signImageUrl}
        />
      </>
    );
  }

  return (
    <MainLayout>
      <ContentCard className={styles.contentCardMarginFix}>
        <div className={styles.signSettingPage}>
          {/* 현재 적용된 서명 섹션 */}
          <div className={styles.signSection}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>현재 적용된 서명</div>
            </div>
            <div className={styles.signPreviewContainer}>
              {isLoading ? (
                <div className={styles.loadingText}>로딩 중...</div>
              ) : signImageUrl ? (
                <div className={styles.signPreview}>
                  <img src={signImageUrl} alt="현재 서명" className={styles.signImage} />
                </div>
              ) : (
                <div className={styles.emptyText}>등록된 서명이 없습니다.</div>
              )}
            </div>
            <Button variant="default" className={styles.registerButton} onClick={handleRegister}>
              등록하기
            </Button>
          </div>
        </div>
      </ContentCard>

      {/* 서명설정 모달 */}
      <SignSettingModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleSignSuccess}
        signImageUrl={signImageUrl}
      />
    </MainLayout>
  );
}

export default SignSettingPage;
